/**
 * MatterFileNotesCard — Brief 100
 *
 * "At a glance" file-note card for the matter detail page. Shows a unified,
 * filterable, spreadsheet-style table of ALL file notes for the matter:
 *   - attendance notes auto-populated from the Bonsai Mail addin (Brief 98)
 *   - manual file notes typed directly here (before BM-H4112)
 * "Add File Snap" opens the attendance note form on this matter (BM-H4112).
 *
 * Backed by GET/POST /api/matters/{matterRef}/file-notes. Sorting and column
 * filtering are done client-side over the fetched rows (backend caps at 2000).
 *
 * BM-H4112: FileSnap is the matter's single record of everything that
 * happened, so it also lists every touchpoint CaseMinder holds elsewhere
 * (filed and sent emails, onboarding, ID checks, CCL, time, post, alerts) as
 * read-only rows. Notes stay first-class: full-strength rows, and a "Notes
 * only" switch. If any touchpoint source failed to load the card says so.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../config/api";
import { toast } from "sonner";
import { errorMessage } from "../utils/errorMessage";
import CallNoteModal from "./CallNoteModal";
import DocumentPreviewDrawer from "./documents/DocumentPreviewDrawer";
import { Camera, Plus, ExternalLink, Loader2, Eye, Search } from "lucide-react";
import { rowHaystack } from "./filesnap/fileSnapRow";
import FileSnapTable from "./filesnap/FileSnapTable";

const TYPE_LABEL = { attendance_note: "Attendance", manual_file_note: "Manual", matter_note: "Matter note" };

// BM-H4112: touchpoint categories, in the order the Type filter lists them.
const TOUCHPOINT_LABEL = {
  email_filed: "Email filed",
  email_sent: "Email sent",
  onboarding: "Onboarding",
  conflict: "Conflict",
  aml: "ID / AML",
  ccl: "CCL",
  form: "Form",
  time: "Time",
  post_in: "Post in",
  post_out: "Post out",
  staff_alert: "Staff alert",
  activity: "Activity",
};

const isNote = (r) => r.type !== "touchpoint";
const rowTypeKey = (r) => (isNote(r) ? r.type : `tp:${r.category}`);
const typeLabel = (key) =>
  key.startsWith("tp:") ? (TOUCHPOINT_LABEL[key.slice(3)] || key.slice(3)) : (TYPE_LABEL[key] || key);

export default function MatterFileNotesCard({ matterRef }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [notesOnly, setNotesOnly] = useState(false);
  const [incomplete, setIncomplete] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);  // BM-H4268
  const [search, setSearch] = useState("");  // BM-H4295

  // BM-H4308: the same toolbar as the File Snap page: search, type, by, dates.
  const [filters, setFilters] = useState({ type: "", by: "", date_from: "", date_to: "" });

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/matters/${encodeURIComponent(matterRef)}/file-notes`,
        { params: { limit: 2000, sort: "date_desc", include_touchpoints: true } }
      );
      setRows(r.data?.rows || []);
      setIncomplete(Boolean(r.data?.touchpoints_incomplete || r.data?.truncated));
    } catch (e) {
      const msg = errorMessage(e, "Could not load File Snaps");
      toast.error(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [matterRef]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const filtered = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((r) => {
      if (notesOnly && !isNote(r)) return false;
      if (filters.type && rowTypeKey(r) !== filters.type) return false;
      if (filters.by && (r.fee_earner_name || "") !== filters.by) return false;
      const day = (r.date || "").slice(0, 10);
      if (filters.date_from && day < filters.date_from) return false;
      if (filters.date_to && day > filters.date_to) return false;
      if (words.length) {
        const hay = rowHaystack(r, [typeLabel(rowTypeKey(r))]);
        if (!words.every((w) => hay.includes(w))) return false;
      }
      return true;
    }).sort((a, b) => ((a.date || "") < (b.date || "") ? 1 : (a.date || "") > (b.date || "") ? -1 : 0));
  }, [rows, filters, notesOnly, search]);

  const noteCount = useMemo(() => rows.filter(isNote).length, [rows]);
  const presentTouchpointKeys = useMemo(
    () => Object.keys(TOUCHPOINT_LABEL).filter((c) => rows.some((r) => r.type === "touchpoint" && r.category === c)),
    [rows]
  );
  const people = useMemo(
    () => [...new Set(rows.map((r) => r.fee_earner_name).filter(Boolean))].sort(),
    [rows]
  );
  const anyFilter = Boolean(search || filters.type || filters.by || filters.date_from || filters.date_to);
  const clearFilters = () => { setSearch(""); setFilters({ type: "", by: "", date_from: "", date_to: "" }); };
  const field = "bg-slate-900/60 border border-white/10 rounded-md px-2.5 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-teal/40";

  return (
    <section className="mt-6 bg-white/[0.02] border border-white/10 rounded-lg p-5"
             data-testid="matter-file-notes-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-white flex items-center gap-2">
          <Camera className="w-4 h-4 text-teal" />
          File Snap
          {/* BM-H4268: "0 + 54 touchpoints" read as nonsense; say it plainly. */}
          <span className="text-xs text-slate-500" data-testid="file-snap-summary">
            {rows.length} entr{rows.length === 1 ? "y" : "ies"}, <span data-testid="file-note-count">{noteCount}</span> note{noteCount === 1 ? "" : "s"}
          </span>
        </h2>
        <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
          <input type="checkbox" checked={notesOnly}
                 onChange={(e) => setNotesOnly(e.target.checked)}
                 data-testid="file-snap-notes-only" />
          Notes only
        </label>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-white/15 text-slate-200 hover:bg-white/[0.05]"
          data-testid="file-note-add-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add File Snap
        </button>
        </div>
      </div>

      {incomplete && (
        <div className="mb-3 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2"
             data-testid="file-snap-incomplete">
          Some touchpoints could not be loaded, so this list may be missing entries. Refresh to try again.
        </div>
      )}

      {/* BM-H4112: an entry typed into File Snap is an attendance note on this
          matter, through the same form as the top bar: filed to Attendances,
          colleague alert, time with the standard line, and the clock. */}
      {showForm && (
        <CallNoteModal
          open
          onClose={() => { setShowForm(false); fetchNotes(); }}
          defaultMatterRef={matterRef}
          initialTab="attendance"
        />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8 text-slate-400" data-testid="file-note-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-500 py-6 text-center" data-testid="file-note-empty">
          No File Snaps yet
        </div>
      ) : (
        <div>
          {/* BM-H4308 (Vanessa, 14 Oct 2026): "can we use same format as the
              all-in page?" Same toolbar and table as File Snap, no Matter column. */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search this matter's File Snap..."
                className={`w-full pl-8 placeholder-slate-500 ${field}`}
                data-testid="file-snap-card-search"
              />
            </div>
            <select value={filters.type} onChange={(e) => setFilter("type", e.target.value)}
                    aria-label="Type" className={field} data-testid="file-note-filter-type">
              <option value="">All types</option>
              <option value="attendance_note">Attendance</option>
              <option value="manual_file_note">File note</option>
              <option value="matter_note">Matter note</option>
              {presentTouchpointKeys.map((c) => (
                <option key={c} value={`tp:${c}`}>{TOUCHPOINT_LABEL[c]}</option>
              ))}
            </select>
            <select value={filters.by} onChange={(e) => setFilter("by", e.target.value)}
                    aria-label="By" className={field} data-testid="file-note-filter-by">
              <option value="">Everyone</option>
              {people.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <label className="flex items-center gap-1 text-xs text-slate-400">
              From
              <input type="date" value={filters.date_from} onChange={(e) => setFilter("date_from", e.target.value)}
                     className={field} data-testid="file-note-date-from" />
            </label>
            <label className="flex items-center gap-1 text-xs text-slate-400">
              To
              <input type="date" value={filters.date_to} onChange={(e) => setFilter("date_to", e.target.value)}
                     className={field} data-testid="file-note-date-to" />
            </label>
          </div>
          <div className="flex items-center gap-3 mb-2 text-xs text-slate-400">
            <span data-testid="file-note-shown">
              {anyFilter || notesOnly ? `Showing ${filtered.length} of ${rows.length}` : `${rows.length} entries`}
            </span>
            {anyFilter && (
              <button type="button" onClick={clearFilters} className="text-teal hover:underline"
                      data-testid="file-note-clear-filters">Clear filters</button>
            )}
          </div>
          {/* Vanessa, 27 Oct 2026: "the filesnap is massive". The table scrolls
              inside a fixed height, so the page below it stays in reach. */}
          <div className="overflow-auto max-h-[26rem] rounded-md border border-white/10" data-testid="file-snap-scroll">
            <FileSnapTable
              rows={filtered}
              whoOf={(r) => r.fee_earner_name || ""}
              dimTouchpoints
              rowTestId={(r) => `file-note-row-${r.id}`}
              textTestId={(r) => `file-note-text-${r.id}`}
              actions={(r) => (
                <span className="flex items-center gap-2">
                  {r.preview_item_id && (
                    <button type="button"
                            className="text-teal hover:text-teal/80"
                            title="Preview"
                            aria-label="Preview"
                            onClick={() => setPreviewDoc({
                              id: r.preview_item_id,
                              title: r.preview_title || r.note_text || "",
                              matter_ref: matterRef,
                              web_url: r.sharepoint_file_url || undefined,
                            })}
                            data-testid={`file-note-preview-${r.id}`}>
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                  {r.sharepoint_file_url && (
                    <a href={r.sharepoint_file_url} target="_blank" rel="noopener noreferrer"
                       className="text-teal hover:text-teal/80" aria-label="Open in SharePoint" title="Open in SharePoint">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </span>
              )}
            />
          </div>
          {filtered.length === 0 && (
            <div className="text-sm text-slate-500 py-6 text-center">No entries match the current filters</div>
          )}
        </div>
      )}
      <DocumentPreviewDrawer doc={previewDoc} strategy="matter" onClose={() => setPreviewDoc(null)} />
    </section>
  );
}
